import { overlayMap } from './map.js'

/* Historical coastline overlays.
 *
 * Driven by public/data/coastlines/manifest.json rather than hardcoded, so a
 * researcher can add a newly georeferenced survey by dropping in a file and
 * adding four lines of JSON — no rebuild, no JavaScript. That matters: the
 * people most likely to find a better 1904 survey sheet are historians, not
 * developers.
 *
 * Two source types are supported:
 *   raster   an XYZ tile template of a georeferenced historical map sheet
 *   geojson  digitised shoreline polygons — the land as it was at that date
 *
 * Everything is added to `overlayMap`, the transparent map stacked over the
 * base. See the comment in map.js for why that second canvas exists.
 */

/* How far the paper wash mutes the modern map beneath a historical sheet.
   Tuned by eye: below about 0.6 the basemap's roads still compete with the
   survey's line work, and at 1.0 the present disappears entirely, which defeats
   the comparison — you need to still see where today's shoreline runs. */
const WASH_STRENGTH = 0.78

let manifest = []
let activeId = null
let opacity = 0.7
let swipeOn = false
let swipeX = 0.5

export const layers = () => manifest

export async function loadManifest() {
  try {
    const res = await fetch('/data/coastlines/manifest.json')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    manifest = (await res.json()).layers ?? []
  } catch (err) {
    // A missing or malformed manifest must not take the map down with it —
    // the coastline is an enrichment, the stories are the point.
    console.warn('[tideline] no coastline manifest:', err.message)
    manifest = []
  }
  return manifest
}

function clearActive() {
  if (!activeId) return
  const entry = manifest.find((l) => l.id === activeId)
  if (entry) {
    if (overlayMap.getLayer(`cl-${entry.id}`)) overlayMap.removeLayer(`cl-${entry.id}`)
    if (overlayMap.getLayer(`cl-${entry.id}-line`)) overlayMap.removeLayer(`cl-${entry.id}-line`)
    if (overlayMap.getLayer(`cl-${entry.id}-wash`)) overlayMap.removeLayer(`cl-${entry.id}-wash`)
    if (overlayMap.getSource(`cl-${entry.id}`)) overlayMap.removeSource(`cl-${entry.id}`)
    if (overlayMap.getSource(`cl-${entry.id}-wash`)) overlayMap.removeSource(`cl-${entry.id}-wash`)
  }
  activeId = null
}

export function show(id) {
  if (!overlayMap) return
  if (id === activeId) return
  clearActive()
  if (!id) return

  const entry = manifest.find((l) => l.id === id)
  if (!entry) return

  const sid = `cl-${entry.id}`

  if (entry.type === 'raster') {
    // A scanned survey is thin grey line work. Laid straight over a full-colour
    // street map, the two just cancel each other out — the basemap's roads and
    // parks read louder than the draughtsman's coastline, which is the one thing
    // you came to look at. So lay a paper-coloured wash over the modern map
    // first, confined to the sheet's own footprint. Inside the rectangle the
    // present recedes to a ghost and the old shoreline reads clearly; outside
    // it, today's map is untouched. The edge of the wash doubles as an honest
    // marker of how far this sheet actually surveyed.
    if (entry.bounds) {
      const [w, s, e, n] = entry.bounds
      overlayMap.addSource(`${sid}-wash`, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]],
          },
        },
      })
      overlayMap.addLayer({
        id: `${sid}-wash`,
        type: 'fill',
        source: `${sid}-wash`,
        paint: { 'fill-color': '#f6f2e8', 'fill-opacity': opacity * WASH_STRENGTH },
      })
    }

    overlayMap.addSource(sid, {
      type: 'raster',
      tiles: [entry.url],
      tileSize: entry.tileSize ?? 256,
      minzoom: entry.minzoom ?? 0,
      maxzoom: entry.maxzoom ?? 18,
      bounds: entry.bounds,
      attribution: entry.attribution ?? '',
    })
    overlayMap.addLayer({
      id: sid,
      type: 'raster',
      source: sid,
      paint: {
        'raster-opacity': opacity,
        'raster-fade-duration': 200,
        // The scans are pencil and pen on yellowed paper, so the ink arrives as
        // mid-grey with a warm cast. Contrast pushes the line work back toward
        // black; dropping the saturation removes the tea-stain tint that would
        // otherwise clash with the basemap's greens and blues. The result reads
        // as a drawing on the modern map rather than a photograph of an old one.
        'raster-contrast': 0.35,
        'raster-saturation': -0.7,
      },
    })
  } else {
    overlayMap.addSource(sid, { type: 'geojson', data: entry.url })
    // Filled land plus a firm edge. The edge is what people actually read —
    // "the shoreline ran here" — so it stays legible at low opacity.
    overlayMap.addLayer({
      id: sid,
      type: 'fill',
      source: sid,
      paint: {
        'fill-color': entry.color ?? '#d9c9a3',
        'fill-opacity': opacity * 0.55,
      },
    })
    overlayMap.addLayer({
      id: `${sid}-line`,
      type: 'line',
      source: sid,
      paint: {
        'line-color': entry.color ?? '#8a7040',
        'line-width': 1.4,
        'line-opacity': opacity,
      },
    })
  }

  activeId = entry.id
}

export function setOpacity(value) {
  opacity = value
  if (!activeId || !overlayMap) return
  const sid = `cl-${activeId}`
  const entry = manifest.find((l) => l.id === activeId)
  if (!overlayMap.getLayer(sid)) return

  if (entry.type === 'raster') {
    overlayMap.setPaintProperty(sid, 'raster-opacity', opacity)
    // The wash fades with the sheet, so sliding to zero returns you to a clean
    // modern map rather than leaving a pale rectangle behind.
    if (overlayMap.getLayer(`${sid}-wash`)) {
      overlayMap.setPaintProperty(`${sid}-wash`, 'fill-opacity', opacity * WASH_STRENGTH)
    }
  } else {
    overlayMap.setPaintProperty(sid, 'fill-opacity', opacity * 0.55)
    overlayMap.setPaintProperty(`${sid}-line`, 'line-opacity', opacity)
  }
}

/* ── Swipe compare ───────────────────────────────────────────────────────── */

let handleEl = null

export function setSwipe(on) {
  swipeOn = on
  applyClip()

  if (on && !handleEl) {
    handleEl = document.createElement('div')
    handleEl.id = 'swipe-handle'
    handleEl.style.left = `${swipeX * 100}%`
    document.getElementById('map').appendChild(handleEl)

    const drag = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX
      swipeX = Math.min(0.98, Math.max(0.02, x / window.innerWidth))
      handleEl.style.left = `${swipeX * 100}%`
      applyClip()
    }
    const stop = () => {
      window.removeEventListener('pointermove', drag)
      window.removeEventListener('pointerup', stop)
    }
    handleEl.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      window.addEventListener('pointermove', drag)
      window.addEventListener('pointerup', stop)
    })
  }

  if (handleEl) handleEl.style.display = on ? 'block' : 'none'
}

/* The whole reason the coastline lives on its own canvas: clip-path can hide
   the right-hand portion of it, revealing today's map underneath, which is
   exactly the "then vs now" gesture. Clipping a MapLibre *layer* this way is
   not possible — only a whole element. */
function applyClip() {
  const host = document.getElementById('coastline-overlay')
  if (!host) return
  host.style.clipPath = swipeOn ? `inset(0 ${(1 - swipeX) * 100}% 0 0)` : 'none'
}

/**
 * Choose the layer that best represents a given year — the most recent survey
 * at or before it. Someone dragging the timeline to 1955 should see the
 * shoreline as it was then, without hunting through a dropdown.
 */
export function bestForYear(year) {
  const eligible = manifest
    .filter((l) => l.year != null && l.year <= year)
    .sort((a, b) => b.year - a.year)
  return eligible[0]?.id ?? null
}
